import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { APP_CONFIG } from '../../config/config';
import { AppConfig } from '../../config/config.type';

export interface CaptionCue {
  start: number;
  end: number;
  text: string;
}

export interface CaptionTrack {
  id: string;
  lang: string;
  label: string;
  url: string;
  status?: string;
  source?: string;
  publishedAt?: string | null;
  youtubeSyncStatus?: 'none' | 'pending' | 'synced' | 'failed' | string;
  youtubeSyncedAt?: string | null;
  youtubeSyncError?: string | null;
  youtubeCaptionId?: string | null;
  cues?: CaptionCue[] | null;
  dubStatus?: 'none' | 'processing' | 'ready' | 'failed' | string;
  dubAudioUrl?: string | null;
  dubError?: string | null;
}

export interface CaptionGenerationJob {
  id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | string;
  progress: number;
  progressMessage?: string | null;
  errorMessage?: string | null;
  sourceLang: string;
  targetLangs: string[];
  resultCaptionIds?: string[] | null;
}

@Injectable({ providedIn: 'root' })
export class CaptionsService {
  private readonly apiUrl: string;

  constructor(
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig,
    private readonly http: HttpClient,
  ) {
    this.apiUrl = this.appConfig.apiUrl;
  }

  listContentCaptions(contentId: string): Observable<CaptionTrack[]> {
    return this.http.get<CaptionTrack[]>(
      [this.apiUrl, 'contents', contentId, 'captions'].join('/'),
    );
  }

  uploadContentCaption(contentId: string, lang: string, file: File): Observable<CaptionTrack> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('lang', lang);
    return this.http.post<CaptionTrack>(
      [this.apiUrl, 'contents', contentId, 'captions'].join('/'),
      formData,
    );
  }

  deleteContentCaption(contentId: string, lang: string): Observable<void> {
    return this.http.delete<void>(
      [this.apiUrl, 'contents', contentId, 'captions', lang].join('/'),
    );
  }

  publishContentCaption(contentId: string, lang: string): Observable<CaptionTrack> {
    return this.http.post<CaptionTrack>(
      [this.apiUrl, 'contents', contentId, 'captions', lang, 'publish'].join('/'),
      {},
    );
  }

  publishContentCaptionToYoutube(contentId: string, lang: string): Observable<CaptionTrack> {
    return this.http.post<CaptionTrack>(
      [this.apiUrl, 'contents', contentId, 'captions', lang, 'publish-youtube'].join('/'),
      {},
    );
  }

  listFormationContentCaptions(formationContentId: string): Observable<CaptionTrack[]> {
    return this.http.get<CaptionTrack[]>(
      [this.apiUrl, 'formation-contents', formationContentId, 'captions'].join('/'),
    );
  }

  uploadFormationContentCaption(
    formationContentId: string,
    lang: string,
    file: File,
  ): Observable<CaptionTrack> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('lang', lang);
    return this.http.post<CaptionTrack>(
      [this.apiUrl, 'formation-contents', formationContentId, 'captions'].join('/'),
      formData,
    );
  }

  deleteFormationContentCaption(formationContentId: string, lang: string): Observable<void> {
    return this.http.delete<void>(
      [this.apiUrl, 'formation-contents', formationContentId, 'captions', lang].join('/'),
    );
  }

  publishFormationContentCaption(
    formationContentId: string,
    lang: string,
  ): Observable<CaptionTrack> {
    return this.http.post<CaptionTrack>(
      [this.apiUrl, 'formation-contents', formationContentId, 'captions', lang, 'publish'].join('/'),
      {},
    );
  }

  publishFormationContentCaptionToYoutube(
    formationContentId: string,
    lang: string,
  ): Observable<CaptionTrack> {
    return this.http.post<CaptionTrack>(
      [
        this.apiUrl,
        'formation-contents',
        formationContentId,
        'captions',
        lang,
        'publish-youtube',
      ].join('/'),
      {},
    );
  }

  startContentCaptionJob(
    contentId: string,
    body: { sourceLang?: string; targetLangs?: string[] } = { sourceLang: 'fr', targetLangs: ['en'] },
  ): Observable<CaptionGenerationJob> {
    return this.http.post<CaptionGenerationJob>(
      [this.apiUrl, 'contents', contentId, 'caption-jobs'].join('/'),
      body,
    );
  }

  getContentCaptionJob(contentId: string, jobId: string): Observable<CaptionGenerationJob> {
    return this.http.get<CaptionGenerationJob>(
      [this.apiUrl, 'contents', contentId, 'caption-jobs', jobId].join('/'),
    );
  }

  dubContentCaption(contentId: string, lang: string): Observable<{ status: string; lang: string }> {
    return this.http.post<{ status: string; lang: string }>(
      [this.apiUrl, 'contents', contentId, 'captions', lang, 'dub'].join('/'),
      {},
    );
  }

  dubFormationContentCaption(
    formationContentId: string,
    lang: string,
  ): Observable<{ status: string; lang: string }> {
    return this.http.post<{ status: string; lang: string }>(
      [this.apiUrl, 'formation-contents', formationContentId, 'captions', lang, 'dub'].join('/'),
      {},
    );
  }

  updateContentCaptionCues(
    contentId: string,
    lang: string,
    cues: CaptionCue[],
  ): Observable<{ id: string; lang: string; cues: CaptionCue[] }> {
    return this.http.put<{ id: string; lang: string; cues: CaptionCue[] }>(
      [this.apiUrl, 'contents', contentId, 'captions', lang, 'cues'].join('/'),
      { cues },
    );
  }

  startFormationContentCaptionJob(
    formationContentId: string,
    body: { sourceLang?: string; targetLangs?: string[] } = { sourceLang: 'fr', targetLangs: ['en'] },
  ): Observable<CaptionGenerationJob> {
    return this.http.post<CaptionGenerationJob>(
      [this.apiUrl, 'formation-contents', formationContentId, 'caption-jobs'].join('/'),
      body,
    );
  }

  getFormationContentCaptionJob(
    formationContentId: string,
    jobId: string,
  ): Observable<CaptionGenerationJob> {
    return this.http.get<CaptionGenerationJob>(
      [this.apiUrl, 'formation-contents', formationContentId, 'caption-jobs', jobId].join('/'),
    );
  }

  updateFormationContentCaptionCues(
    formationContentId: string,
    lang: string,
    cues: CaptionCue[],
  ): Observable<{ id: string; lang: string; cues: CaptionCue[] }> {
    return this.http.put<{ id: string; lang: string; cues: CaptionCue[] }>(
      [this.apiUrl, 'formation-contents', formationContentId, 'captions', lang, 'cues'].join('/'),
      { cues },
    );
  }
}
